import {
  useNavigate,
  useLocation,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from "react-router-dom";
import { useMemo, useCallback } from "react";

export { useNavigate };

/**
 * Compatibility stub for `redirect()` from `next/navigation` (server-only in Next).
 * In SPA builds, prefer `useNavigate()` or `<Navigate />`.
 */
export function redirect(href: string): never {
  if (typeof window !== "undefined") {
    window.location.assign(href);
  }
  throw new Error(`NEXT_REDIRECT:${href}`);
}

export function usePathname(): string {
  return useLocation().pathname;
}

export function useSearchParams(): URLSearchParams {
  const [params] = useRRSearchParams();
  return params;
}

export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  return useRRParams() as T;
}

type NavigateOptions = { scroll?: boolean; replace?: boolean };

export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();

  const push = useCallback(
    (href: string, _options?: NavigateOptions) => {
      navigate(href);
    },
    [navigate],
  );

  const replace = useCallback(
    (href: string, _options?: NavigateOptions) => {
      navigate(href, { replace: true });
    },
    [navigate],
  );

  const prefetch = useCallback(async (_href: string) => {
    /* SPA: optional route prefetch could be wired here */
  }, []);

  return useMemo(
    () => ({
      push,
      replace,
      prefetch,
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => navigate(0),
      pathname: location.pathname,
    }),
    [push, replace, prefetch, navigate, location.pathname],
  );
}
